import { Investors } from '@components/book-trial-class-jp/investors'
import CodingCertificate from '@components/common/coding-certificate'
import CodingForStudents from '@components/common/coding-for-students'
import Footer from '@components/common/footer'
import Navbar from '@components/common/navbar'
import Activities from '@components/course-landing-page/activities'
import ClassCurriculum from '@components/course-landing-page/class-curriculum'
import ContentCard from '@components/course-landing-page/content-card'
import CourseDescription from '@components/course-landing-page/course-description'
import CourseFeatures from '@components/course-landing-page/course-features'
import Faq from '@components/course-landing-page/faq'
import FirstFold from '@components/course-landing-page/first-fold'
import FooterBanner from '@components/course-landing-page/footer-banner'
import RecommendedCourses from '@components/course-landing-page/recommended-courses'
import StemAccredited from '@components/course-landing-page/stem-accredited'
import Teachers from '@components/course-landing-page/teachers'
import { COURSE_INFO } from '@components/locale-pages/home/courses'
import VideoTestimonial from '@components/locale-pages/home/video-testimonial'
import useStoreUTMParams from '@hooks/useStoreUTMParam'
import { COURSE_NAMES, FAQ } from '@lib/data/course-landing-page'
import { VIDEO_TESTIMONIAL } from '@lib/data/summer-coding-camp'
import { Trans } from '@lingui/macro'
import { CourseJsonLd, FAQPageJsonLd, NextSeo } from 'next-seo'
import dynamic from 'next/dynamic'
import CodingalProductBlog from '@components/blog/product-blogs'
import { useState } from 'react'
import { formatSeoConfig } from '@lib/seo'
import TrustpilotReview from '@components/locale-pages/home/trustpilot-review'
import FastlyImage from '@components/common/fastly-image'

const CourseCard = dynamic(() =>
  import('@components/platform-course-landing-page/course-card')
)

const { AICODE101 } = COURSE_NAMES
const urlQuery = `?course=${COURSE_INFO['aigc'].trialCourseQuery}`

export const AICODE101Seo = formatSeoConfig({
  title: 'AI Code 101 for Kids: Live AI Coding Classes',
  description:
    'AI Code 101 for kids, taught by expert instructors. Build interactive AI projects and explore Artificial Intelligence at Codingal. Try a free lesson.',
  url: 'https://www.codingal.com/courses/ai-code-101-for-kids ',
  metaImage:
    'https://fastly.codingal.com/images/courses/og/codingal-dark-v3.jpg',
})

const BegineerGuide = () => (
  <div className="space-y-3">
    <p>
      In our Scratch + AI Code 101 - Introduction to AI Models module, kids get
      to dive into hands-on projects where they can have fun and build their own
      creations! Here are some exciting projects they will work on:
    </p>
    <div>
      <FastlyImage
        src={`/images/courses/ai-code-101/ai-code-101-1.jpg`}
        alt="AI Code 101 projects for kids"
        width={512}
        height={341}
      />
    </div>
    <ul>
      <li>
        Name It to Win It: Train an AI to recognize objects and test how
        accurately it can identify them.
      </li>
      <li>
        Waste Wise: Sorting the Planet’s Burden: Create an AI that can classify
        waste into recyclables and non-recyclables, helping the planet!
      </li>
      <li>
        Audio Explorers: GK Challenge: Develop an AI that listens to questions
        and responds with trivia, turning it into a fun game.
      </li>
      <li>
        Chatbot Quest: Build a chatbot that can answer questions, interact with
        users, and hold conversations.
      </li>
      <li>
        TravelBot Trek: Discover the World with AI: Create an AI-powered travel
        assistant that can guide users through different destinations and
        recommend activities.
      </li>
      <li>
        PetPal: Design an AI-powered virtual pet that learns and responds to its
        owner, creating an interactive pet experience.
      </li>
    </ul>
    <p>
      These projects encourage kids to experiment, learn, and have fun while
      exploring the basics of AI!
    </p>
    <p>
      Artificial Intelligence (AI) can be explained to kids as computers
      learning to think and solve problems like humans. It’s like giving a
      computer a brain: it can learn from experience, recognize patterns, and
      make decisions. Teaching your child AI early empowers them to explore
      technology creatively while developing critical thinking.
    </p>
    <h3 className="font-600 text-lg lg:text-xl">
      1. Understand What is AI for Kids?
    </h3>
    <div>
      <FastlyImage
        src={`/images/courses/ai-code-101/ai-for-kids.jpg`}
        alt="Child learning AI concepts with a robot, showing how computers can think and learn like humans"
        width={512}
        height={341}
      />
    </div>
    <ol>
      <li>AI makes computers smarter and more capable.</li>
      <li>
        Unlike regular programs, AI learns from data and improves over time.
      </li>
      <li>
        Think of it like teaching a dog a trick: practice and repetition help it
        learn.
      </li>
      <li>
        Real-world examples include voice assistants like Siri or Alexa, and
        self-driving cars that “see” the road and make decisions.
      </li>
    </ol>
    <h3 className="font-600 text-lg lg:text-xl">
      2. Explore AI Through Fun Activities
    </h3>
    <div>
      <FastlyImage
        src={`/images/courses/ai-code-101/explore-ai.jpg`}
        alt="Child and parent exploring AI through fun games and movies"
        width={512}
        height={341}
      />
    </div>
    <ul>
      <li>
        Watch AI in movies like Wall-E or play games with AI mods in Minecraft.
      </li>
      <li>
        Talk with your child about how the AI adapts to actions, makes choices,
        or solves problems.
      </li>
      <li>These fun examples make AI relatable and spark curiosity.</li>
    </ul>
    <h3 className="font-600 text-lg lg:text-xl">
      3. Encourage Critical Thinking
    </h3>
    <div>
      <FastlyImage
        src={`/images/courses/ai-code-101/critical-thinking.jpg`}
        alt="Parent guiding child to think critically about AI recommendations on apps and websites"
        width={512}
        height={341}
      />
    </div>
    <ul>
      <li>
        Discuss how AI recommendations work, such as Netflix or YouTube
        suggestions.
      </li>
      <li>
        Ask your child why certain content appears and what factors influence
        it.
      </li>
      <li>
        Help them understand that AI is powerful but not always perfect, and
        critical thinking is key.
      </li>
    </ul>
    <h3 className="font-600 text-lg lg:text-xl">
      4. Teach Ethics and Fairness
    </h3>
    <div>
      <FastlyImage
        src={`/images/courses/ai-code-101/teach-ethics.jpg`}
        alt="Teaching kids about AI ethics and fairness using simple examples"
        width={512}
        height={341}
      />
    </div>
    <ul>
      <li>
        Explain that AI can sometimes be biased depending on the data it is
        trained with.
      </li>
      <li>
        Use simple examples, like hiring algorithms or facial recognition
        systems, to discuss fairness.
      </li>
      <li>
        Teaching kids about AI ethics develops responsibility and awareness of
        technology’s impact.
      </li>
    </ul>
    <h3 className="font-600 text-lg lg:text-xl">5. Make AI Hands-On and Fun</h3>
    <div>
      <FastlyImage
        src={`/images/courses/ai-code-101/ai-hands-on.jpg`}
        alt="Child creating an AI-powered game using Scratch and learning AI concepts hands-on"
        width={512}
        height={341}
      />
    </div>
    <ul>
      <li>
        Platforms like Scratch, AI Code 101, and Machine Learning for Kids offer
        kid-friendly tutorials.
      </li>
      <li>
        Kids can build AI-driven games, chatbots, or creative projects to see AI
        in action.
      </li>
      <li>Hands-on projects make AI exciting, tangible, and educational.</li>
    </ul>
    <h3 className="font-600 text-lg lg:text-xl">6. Teach Digital Safety</h3>
    <div>
      <FastlyImage
        src={`/images/courses/ai-code-101/digital-safety.jpg`}
        alt="Teaching kids digital safety and privacy when interacting with AI online"
        width={512}
        height={341}
      />
    </div>
    <ul>
      <li>
        Explain how AI can collect data and why personal information should be
        protected.
      </li>
      <li>Teach strong passwords and responsible online behavior.</li>
      <li>
        Make sure kids know to ask a trusted adult before sharing sensitive
        information.
      </li>
    </ul>
    <h3 className="font-600 text-lg lg:text-xl">
      7. Learn With AI Educational Apps
    </h3>
    <div>
      <FastlyImage
        src={`/images/courses/ai-code-101/learn-with-ai.jpg`}
        alt="Children learning AI concepts through educational apps and interactive projects"
        width={512}
        height={341}
      />
    </div>
    <p>
      Kids can explore AI concepts through fun, hands-on learning with apps and
      websites designed for beginners.
    </p>
    <ul>
      <li>
        Kodable teaches coding logic and AI basics with colorful games and
        challenges.
      </li>
      <li>
        Machine Learning for Kids lets kids build simple AI models, like image
        or text classifiers, in a visual and playful way.
      </li>
      <li>
        Scratch for kids can create interactive stories, games, and simple AI
        experiments using drag-and-drop blocks.
      </li>
      <li>
        Tynker offers beginner-friendly AI coding projects that guide kids step
        by step.
      </li>
    </ul>
    <p>
      These apps let children see how AI works in real life, try experiments
      safely, and learn by doing, all in a fun, interactive way.
    </p>
    <h3 className="font-600 text-lg lg:text-xl">
      8. Explore AI in Arts and Music
    </h3>
    <div>
      <FastlyImage
        src={`/images/courses/ai-code-101/ai-art-music.jpg`}
        alt="Children exploring AI-generated art and music using simple tools"
        width={512}
        height={341}
      />
    </div>
    <p>
      AI can create music, generate art, or produce interactive storytelling
      experiences.
    </p>
    <ul>
      <li>
        Encourage kids to try Quick, fun AI drawing tools like AutoDraw or
        Sketchpad, which turn doodles into neat digital artwork.
      </li>
      <li>
        Let them experiment with AI music tools for beginners like Chrome Music
        Lab or BeepBox, which let kids make tunes using simple interfaces.
      </li>
      <li>
        Kids can explore story creation with AI using StoryJumper or AI story
        prompts that help them build interactive stories easily.
      </li>
    </ul>

    <h3 className="font-600 text-lg lg:text-xl">
      9. Benefits of Learning AI Code 101 for Kids
    </h3>
    <div>
      <FastlyImage
        src={`/images/courses/ai-code-101/ai-code-101-2.jpg`}
        alt="Benefits of learning AI Code 101 for kids"
        width={512}
        height={341}
      />
    </div>
    <ul>
      <li>
        Boost creativity by designing their own AI projects, like interactive
        games or smart apps
      </li>
      <li>
        Develop problem-solving skills by figuring out how AI models work and
        making decisions based on data
      </li>
      <li>
        Understand the basics of AI and coding, which are essential skills for
        future tech careers
      </li>
      <li>
        Express themselves by building their own AI-powered projects and
        experiments
      </li>
      <li>
        AI Code 101 gives kids a strong foundation for learning more advanced
        coding and AI concepts, including machine learning, Python programming,
        and data-driven decision-making.
      </li>
    </ul>
    <h3 className="font-600 text-lg lg:text-xl">
      10.Take the First Step Together
    </h3>
    <div>
      <FastlyImage
        src={`/images/courses/ai-code-101/first-step.jpg`}
        alt="Parent and child starting a free AI for kids trial lesson with Codingal tutors"
        width={512}
        height={341}
      />
    </div>
    <p>
      Start small by trying an interactive tutorial or a simple AI project.
      Support your child as they explore, experiment, and learn from mistakes,
      and encourage curiosity, creativity, and fun while navigating the world of
      AI.
    </p>
    <p>
      Start with a free AI for kids trial lesson at Codingal with our
      experienced Computer Science tutors, and watch your child explore, create,
      and learn the magic of AI in a fun, interactive way!
    </p>
  </div>
)
const AICode101 = () => {
  const [shouldUpdateCourseCard, setShouldUpdateCourseCard] = useState(false)

  const handleHideCourseCard = (value: boolean) => {
    setShouldUpdateCourseCard(value)
  }

  useStoreUTMParams()

  return (
    <>
      <NextSeo {...AICODE101Seo} />
      <FAQPageJsonLd
        mainEntity={FAQ[AICODE101].map(
          ({ acceptedAnswerText, questionName }) => ({
            acceptedAnswerText: acceptedAnswerText,
            questionName: questionName,
          })
        )}
      />
      <CourseJsonLd
        courseName="Learn AI Code 101 with Top AI & Coding Course Instructors for Kids and Teens"
        description={`At Codingal, we make learning AI an exciting adventure for kids! AI Code 101 for Kids introduces children to the world of artificial intelligence through fun, interactive projects that spark creativity and curiosity. Kids will learn how to build their own AI-powered applications, like chatbots or virtual travel bots, that solve real-world problems in engaging ways. In our AI Genius course, kids will explore the "Scratch + AI Code 101 - Introduction to AI Models" module, learning the basics of AI, such as training machines to recognize patterns, make decisions, and interact with the world. The real magic happens when they realize that they’re not just playing games but actually building the future, one amazing AI project at a time!`}
        provider={{
          '@type': 'Organization',
          name: 'Codingal',
        }}
        offers={{
          '@type': 'Offer',
          category: 'Paid',
        }}
        hasCourseInstance={{
          '@type': 'CourseInstance',
          courseMode: 'Online',
          courseWorkload: 'PT44H',
        }}
      />
      <Navbar tryFreeClassLink={`/register/${urlQuery}`} />
      <FirstFold
        getShouldHideCourseCard={handleHideCourseCard}
        courseName={AICODE101}
        urlQuery={urlQuery}
        isPlatformPage
      />
      <div className="container flex justify-between pt-10 mx-auto px-5 lg:px-10 text-grey">
        <div className="pb-10 md:w-4/5 md:pr-4 max-w-3xl">
          <CourseDescription courseName={AICODE101} />
          <CourseFeatures
            courseTitle={AICODE101}
            title={
              <Trans>Engaging AI Code 101 programming lessons for kids</Trans>
            }
          />
          <ClassCurriculum
            courseName={AICODE101}
            title="Our AI for Kids Class Curriculum"
            description={
              <>
                <p>
                  <Trans>
                    Our comprehensive AI Code 101 curriculum is designed to take
                    young learners on an exciting journey through the world of
                    artificial intelligence and coding. Starting with AI Basics,
                    kids discover fundamental concepts by building projects like
                    a Decision Tree system, teaching computers to recognize
                    happy and sad dogs, creating day/night detection systems,
                    and developing their very own Alien Language translator and
                    Owl Chatbot. As they progress to Advanced AI Projects,
                    students tackle more complex challenges including building a
                    Jargon Buster language tool, designing a Sorted House
                    organization system, creating tourist information helpers,
                    developing Face Lock security systems, and constructing a
                    Feeling Meter that detects emotions.
                  </Trans>
                </p>
                <p>
                  <Trans>
                    Our specialized AI Genius Track culminates the learning
                    experience with hands-on projects like &ldquo;Name It to Win
                    It!&rdquo; recognition games, &ldquo;Waste Wise&rdquo;
                    environmental sorting challenges, &ldquo;Audio
                    Explorers&rdquo; voice-powered quizzes, advanced chatbot
                    development, AI travel guides, and creating their own
                    digital PetPal companion. Throughout all 20+ interactive
                    projects, students use simple block-based coding to bring
                    complex AI concepts to life, ensuring they build both
                    technical skills and creative problem-solving abilities
                    while having fun with cutting-edge technology.
                  </Trans>
                </p>
              </>
            }
          />
          <Activities courseName={AICODE101} />
          <Teachers
            urlQuery={urlQuery}
            title={
              <Trans>
                Learn from experienced teachers. Our hand-picked AI & coding
                tutors ensure the best learning experience
              </Trans>
            }
            description={
              <Trans>
                <p>
                  Our instructors bring deep expertise in Computer Science and
                  complete rigorous training before they teach. They’re truly
                  amazing, and it shows in our exceptional Net Promoter Score
                  (NPS) of 86! Students and parents consistently rate our
                  teachers 4.9 out of 5, and our curriculum earns a fantastic
                  4.8 out of 5. We’re so proud to have a faculty that’s 91%
                  female and 9% male, creating an inclusive, encouraging
                  learning environment where everyone feels welcome. Each tutor
                  is trained to understand individual student needs and deliver
                  personalized support, even in group settings. Plus, students
                  have 24/7 access to expert tutors for instant doubt
                  resolution, anytime they need help. This makes learning a
                  truly supported experience, especially through our virtual AI
                  lessons that connect kids with top educators worldwide.
                </p>
              </Trans>
            }
          />
          <ContentCard
            title={
              <Trans>
                The best place for kids to learn the basics and fundamentals of
                Artificial Intelligence
              </Trans>
            }
            description={
              <Trans>
                <span className="block">
                  At Codingal, we create the perfect environment for kids to
                  explore the world of artificial intelligence using
                  beginner-friendly AI Code 101 block-based programming. Our
                  curriculum is designed to make learning fun and effective,
                  combining hands-on projects, creative coding challenges, and
                  real-world tools that help young learners master the
                  fundamentals of AI.
                </span>
              </Trans>
            }
            courseName={AICODE101}
          />
          <StemAccredited
            urlQuery={urlQuery}
            heading="STEM.org accredited educational experience and certificates"
            description={
              <>
                <p>
                  At Codingal, your child masters AI Code 101 through a STEM.org
                  accredited program built on proven educational frameworks like
                  Bloom&lsquo;s Taxonomy, BIDE, and STEAM. Our curriculum is
                  aligned with Common Core, state standards, AP Computer Science
                  goals, and the K–12 Computer Science Framework, ensuring
                  learning that&lsquo;s both age-appropriate and college-ready.
                  Through hands-on, project-based learning, students create
                  real-world AI applications like emotion detection systems and
                  intelligent chatbots, applying math, science, and engineering
                  logic to solve everyday problems. Backed by STEM.org&lsquo;s
                  rigorous audits, every AI lesson lays a strong, future-proof
                  foundation in STEM.
                </p>
              </>
            }
          />
          <CodingCertificate
            title="Unlock your skills and earn your AI certifications for kids"
            description={
              <>
                Students progress through comprehensive AI modules, from AI
                Basics to Advanced AI Projects, starting with fundamental
                concepts like decision trees and advancing to sophisticated
                applications such as emotion detection and voice recognition
                systems. They learn everything from image classification and
                chatbot development to face recognition and intelligent
                automation, turning lessons into impressive AI-powered projects
                and interactive digital experiences. Upon successful completion,
                students earn the STEM.org certified AI Code 101 Certificate, a
                valuable credential for academic portfolios, school projects,
                and future tech applications. With cutting-edge AI skills and a
                recognized certificate, learners graduate with confidence, a
                strong portfolio of 20+ AI projects, and a competitive edge for
                future studies and careers in the rapidly growing field of
                artificial intelligence.
              </>
            }
            imagePath="/images/courses/ai-and-ml/cetrificate"
            imageWidth={230}
            imageHeight={195}
          />
          <CodingForStudents
            title="Our AI classes for kids are appropriate for middle school & high school students"
            description={
              <>
                At Codingal, our AI & coding curriculum takes elementary and
                middle school students from their very first &ldquo;Hello,
                AI!&rdquo; project to build fully functional intelligent
                systems. We begin with beginner-friendly challenges that
                introduce AI concepts like decision trees, image recognition,
                and simple chatbots through interactive, hands-on activities. As
                students progress, they explore more advanced topics like
                emotion detection, voice processing, and face recognition,
                ensuring a smooth, steady learning curve that builds confidence
                at every step. With every AI project, from teaching computers to
                sort waste to creating digital pet companions, they strengthen
                their computational thinking foundation and gain the excitement
                to take on increasingly amazing AI challenges that prepare them
                for tomorrow&lsquo;s tech world.
              </>
            }
            imageWidth={235}
            imageHeight={241}
            imagePath="/images/courses/ai-and-ml/ai-course-students"
          />
          <VideoTestimonial
            isCoursepage
            title={
              <Trans>
                What students and parents have to say about our training program
              </Trans>
            }
            video_testimonial={VIDEO_TESTIMONIAL}
          />
          <TrustpilotReview isCoursepage urlQuery={urlQuery} />
          <Investors isCoursePage urlQuery={urlQuery} />
          <RecommendedCourses courseCodes={['cgm', 'gd', 'aigc']} />
          <ContentCard
            urlQuery={urlQuery}
            title={
              <>
                Artificial Intelligence for Kids : A Beginner’s Guide for
                Parents
              </>
            }
            description={<BegineerGuide />}
          />
          <Faq
            courseName={AICODE101}
            title="Frequently Asked Questions (FAQs) about AI for Kids"
          />
          <CodingalProductBlog
            urlQuery={urlQuery}
            isCoursePage
            title="Latest from Our Blog"
          />
        </div>
        <div className="hidden ml-4 md:block w-80">
          <div className="pb-5 sticky top-0 pt-5 z-40 w-80">
            {shouldUpdateCourseCard && (
              <CourseCard
                shouldHideCardImage={true}
                courseName={AICODE101}
                urlQuery={urlQuery}
              />
            )}
          </div>
        </div>
      </div>
      <FooterBanner
        title="AI Code 101 for kids with the best Computer Science teachers"
        url={`/register/${urlQuery}`}
      />
      <Footer />
    </>
  )
}

// TODO: Remove once getInitialProps is removed from _app.tsx
export const getStaticProps = async () => {
  return {
    props: {},
  }
}

export default AICode101
